import React from 'react';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Flex,
  Space,
  Steps,
  Tag,
  Tooltip,
  Typography,
  theme,
} from '@duxton-web/ui';
import {
  CloseCircleFilled,
  DocumentOutlined,
  LaunchUniversal,
  StopUniversal,
  TickMarkFilled,
  WarningFilled,
} from '@duxton-web/icons';
import { getCampaignUrl } from './debuggingToolsConstants';

export interface JourneyRecord {
  userID: string;
  rfSubmissionDate: string | null;
  rfPlannedDispatchedTime: string | null;
  dispatchedTime: string;
  templateID: string;
  rfStatus: string;
  xcoStatus: string;
  trackingID: string | null;
  ucdCampaignID: string;
  ucdCampaignName: string;
  ucdTrackingID: string | null;
  tridentCampaignID: string | null;
  hedwigMessageID: string | null;
  hedwigStreamtime: string | null;
  hedwigEventtype: string | null;
  hedwigHTTPCode: number | null;
  hedwigStatusCode: string | null;
  hedwigStatusDetails: string | null;
  hedwigCallingService: string | null;
  hedwigMessage: string | null;
  score: number | null;
  modelSource: string;
  tridentTriggerTime: string;
  tridentRequestTime: string;
  triggerToExecutionSeconds: number | null;
  requestToExecutionSeconds: number | null;
  year: string | null;
  month: string | null;
  day: string | null;
}

export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = dayjs(value);
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : '-';
};

const formatDateOnly = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = dayjs(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : '-';
};

type StatusTone = 'success' | 'error' | 'warning' | 'default';

type DescriptionItem = {
  key: string;
  label: React.ReactNode;
  children: React.ReactNode;
};

type FunnelStage = {
  key: string;
  title: string;
  statusLabel: string;
  tone: StatusTone;
  content: React.ReactNode;
};

type Outcome = {
  result: string;
  reason: string;
  droppedAt: string;
  tone: StatusTone;
  timestamp: string;
};

type TitleStatus = {
  label: string;
  tone: StatusTone;
};

const DELIVERY_SUCCESS_STATUSES = ['sent', 'delivered', 'success'];

const httpCodeTag = (code: number | null) => {
  if (code == null) return <React.Fragment>-</React.Fragment>;
  let color = 'red';
  if (code < 300) {
    color = 'green';
  } else if (code < 500) {
    color = 'orange';
  }
  return <Tag color={color}>{code}</Tag>;
};

const statusTag = (value: string | null | undefined, successValues: string[]) => {
  if (!value) return <React.Fragment>-</React.Fragment>;
  return (
    <Tag color={successValues.includes(value.toLowerCase()) ? 'green' : 'red'}>
      {humanizeStatus(value)}
    </Tag>
  );
};

const humanizeStatus = (value: string | null | undefined): string => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getContentUrl = (templateID: string): string =>
  `${window.Grab.config.HEDWIG_DASH_URI}/templates/create/push/?id=${templateID}`;

const compactText = (value: string, visibleChars = 28): string => {
  if (value.length <= visibleChars) return value;
  const prefixLength = Math.max(8, Math.floor(visibleChars * 0.6));
  const suffixLength = Math.max(4, visibleChars - prefixLength - 1);
  return `${value.slice(0, prefixLength)}…${value.slice(-suffixLength)}`;
};

const getDeliveryStatus = (record: JourneyRecord): string | null =>
  record.hedwigStatusDetails || record.hedwigStatusCode || null;

const isDeliverySuccess = (record: JourneyRecord): boolean => {
  const status = getDeliveryStatus(record);
  return status ? DELIVERY_SUCCESS_STATUSES.includes(status.toLowerCase()) : false;
};

const getTitleStatus = (record: JourneyRecord): TitleStatus => {
  const deliveryStatus = getDeliveryStatus(record);
  if (
    deliveryStatus &&
    DELIVERY_SUCCESS_STATUSES.includes(deliveryStatus.toLowerCase())
  ) {
    return {
      label: humanizeStatus(deliveryStatus),
      tone: 'success',
    };
  }

  return {
    label: 'Dropped',
    tone: 'error',
  };
};

const hasDeliveryError = (record: JourneyRecord): boolean => {
  const status = getDeliveryStatus(record);
  if (record.hedwigHTTPCode != null && record.hedwigHTTPCode >= 400) return true;
  return Boolean(status && !DELIVERY_SUCCESS_STATUSES.includes(status.toLowerCase()));
};

const isXCOFrequencyCapped = (record: JourneyRecord): boolean =>
  record.xcoStatus === 'DROPPED_FREQ_CAP';

const getOutcome = (record: JourneyRecord): Outcome => {
  if (record.rfStatus === 'FAILED_RF') {
    return {
      result: 'Dropped',
      reason: 'Relevance filter failed',
      droppedAt: 'Relevance filter',
      tone: 'error',
      timestamp: formatDate(record.rfSubmissionDate || record.dispatchedTime),
    };
  }

  if (isXCOFrequencyCapped(record)) {
    return {
      result: 'Dropped',
      reason: 'XCO frequency cap triggered',
      droppedAt: 'XCO',
      tone: 'error',
      timestamp: formatDate(record.dispatchedTime),
    };
  }

  if (hasDeliveryError(record)) {
    return {
      result: 'Dropped',
      reason: humanizeStatus(getDeliveryStatus(record)),
      droppedAt: 'Delivery',
      tone: 'error',
      timestamp: formatDate(record.hedwigStreamtime || record.dispatchedTime),
    };
  }

  if (isDeliverySuccess(record)) {
    return {
      result: humanizeStatus(getDeliveryStatus(record)),
      reason: 'Delivered',
      droppedAt: '-',
      tone: 'success',
      timestamp: formatDate(record.hedwigStreamtime || record.dispatchedTime),
    };
  }

  if (record.xcoStatus === 'SELECTED_FOR_DISPATCH') {
    return {
      result: 'Dispatched',
      reason: 'Waiting for delivery',
      droppedAt: '-',
      tone: 'warning',
      timestamp: formatDate(record.dispatchedTime),
    };
  }

  return {
    result: 'Unknown',
    reason: 'No terminal event found',
    droppedAt: '-',
    tone: 'default',
    timestamp: formatDate(record.dispatchedTime),
  };
};

const getToneColor = (tone: StatusTone): string => {
  switch (tone) {
    case 'success':
      return 'green';
    case 'error':
      return 'red';
    case 'warning':
      return 'orange';
    default:
      return 'default';
  }
};

const getStepStatus = (tone: StatusTone): string => {
  switch (tone) {
    case 'success':
      return 'finish';
    case 'error':
      return 'error';
    case 'warning':
      return 'process';
    default:
      return 'wait';
  }
};

const getStatusIcon = (
  tone: StatusTone,
  defaultIcon?: React.ReactNode,
): React.ReactNode => {
  switch (tone) {
    case 'success':
      return <TickMarkFilled />;
    case 'error':
      return <CloseCircleFilled />;
    case 'warning':
      return <WarningFilled />;
    default:
      return defaultIcon;
  }
};

const TruncatedId: React.FC<{ value: string | null }> = ({ value }) => {
  if (!value) return <React.Fragment>-</React.Fragment>;
  const isLong = value.length > 36;
  const display = compactText(value, 36);
  return (
    <Tooltip title={isLong ? value : undefined}>
      <Typography.Metadata copyable={{ text: value }}>
        {display}
      </Typography.Metadata>
    </Tooltip>
  );
};

const StatusTag: React.FC<{ tone: StatusTone; children: React.ReactNode }> = ({
  tone,
  children,
}) => {
  const icon = getStatusIcon(tone);

  return (
    <Tag color={getToneColor(tone)} icon={icon}>
      {children}
    </Tag>
  );
};

const StageTitle: React.FC<{
  title: string;
  statusLabel: string;
  tone: StatusTone;
}> = ({ title, statusLabel, tone }) => (
  <Flex align="center" gap={8} wrap="wrap">
    <Typography.Title level={5} style={{ margin: 0 }}>
      {title}
    </Typography.Title>
    <StatusTag tone={tone}>{statusLabel}</StatusTag>
  </Flex>
);

const StepMarker: React.FC<{ tone: StatusTone; token: any }> = ({ tone, token }) => {
  const toneStyles = {
    success: {
      background: token.colorSuccessBg || '#e8fff1',
      border: token.colorSuccessBorder || '#7be7a2',
      color: token.colorSuccess || '#008f46',
    },
    error: {
      background: token.colorErrorBg || '#fff1f0',
      border: token.colorErrorBorder || '#ff8a86',
      color: token.colorError || '#d42e1c',
    },
    warning: {
      background: token.colorWarningBg || '#fff7e6',
      border: token.colorWarningBorder || '#ffd591',
      color: token.colorWarning || '#f09800',
    },
    default: {
      background: token.colorFillTertiary || '#f5f5f5',
      border: token.colorBorder || '#d9d9d9',
      color: token.colorTextTertiary || '#8c8c8c',
    },
  }[tone];
  const icon = getStatusIcon(tone, <StopUniversal />);

  return (
    <span
      style={{
        alignItems: 'center',
        background: toneStyles.background,
        border: `1px solid ${toneStyles.border}`,
        borderRadius: '50%',
        color: toneStyles.color,
        display: 'inline-flex',
        height: 28,
        justifyContent: 'center',
        width: 28,
      }}
    >
      {icon}
    </span>
  );
};

const DetailDescriptions: React.FC<{
  items: DescriptionItem[];
}> = ({ items }) => (
  <Descriptions
    colon={false}
    column={1}
    items={items}
    size="small"
    styles={{
      content: { textAlign: 'right' },
      label: { width: 170 },
    }}
  />
);

const DetailCard: React.FC<{
  children: React.ReactNode;
  token: any;
}> = ({ children, token }) => (
  <Card
    size="small"
    style={{
      borderColor: token.colorBorderSecondary,
      marginTop: 8,
    }}
    styles={{ body: { padding: '8px 12px' } }}
  >
    {children}
  </Card>
);

const MetadataItem: React.FC<{
  label: string;
  children: React.ReactNode;
  token: any;
}> = ({ label, children, token }) => (
  <div
    style={{
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: token.borderRadiusLG,
      minWidth: 0,
      padding: '10px 12px',
    }}
  >
    <Typography.Metadata type="secondary">{label}</Typography.Metadata>
    <div style={{ marginTop: 4 }}>{children}</div>
  </div>
);

const textOrMissing = (value: string | null | undefined): React.ReactNode =>
  (value || <Typography.Text type="secondary">-</Typography.Text>);

const scoreValue = (score: number | null | undefined): string =>
  (score == null ? '-' : score.toFixed(6));

const relevancyTone = (record: JourneyRecord): StatusTone => {
  if (record.rfStatus === 'PASSED_RF') return 'success';
  if (record.rfStatus === 'FAILED_RF') return 'error';
  return 'default';
};

const xcoTone = (record: JourneyRecord): StatusTone => {
  if (record.rfStatus === 'FAILED_RF') return 'default';
  if (record.xcoStatus === 'SELECTED_FOR_DISPATCH') return 'success';
  if (isXCOFrequencyCapped(record)) return 'error';
  if (record.xcoStatus) return 'warning';
  return 'default';
};

const deliveryTone = (record: JourneyRecord): StatusTone => {
  if (isDeliverySuccess(record)) return 'success';
  if (hasDeliveryError(record)) return 'error';
  if (record.xcoStatus === 'SELECTED_FOR_DISPATCH') return 'warning';
  return 'default';
};

const stageLabel = (tone: StatusTone, successLabel = 'Pass'): string => {
  if (tone === 'success') return successLabel;
  if (tone === 'error') return 'Dropped';
  if (tone === 'warning') return 'Pending';
  return 'Not reached';
};

const getRelevancyStageLabel = (record: JourneyRecord): string => {
  const tone = relevancyTone(record);
  if (tone === 'default') return 'Unknown';
  return stageLabel(tone);
};

const getCampaignType = (modelSource: string | null | undefined): string => {
  const normalizedModel = modelSource ? modelSource.toUpperCase() : '';
  if (normalizedModel === 'SC') return 'SmartComms';
  if (normalizedModel === 'TRIDENT') return 'Trident';
  return '-';
};

const getDailyRetries = (record: JourneyRecord): string =>
  (isXCOFrequencyCapped(record) ? 'Dropped and retried the next day' : '-');

const getXcoStageStatusLabel = (record: JourneyRecord): string => {
  if (record.xcoStatus === 'SELECTED_FOR_DISPATCH') return 'Dispatched';
  if (record.xcoStatus) return humanizeStatus(record.xcoStatus);
  return 'Not reached';
};

const getDeliveryStageLabel = (record: JourneyRecord): string => {
  if (isDeliverySuccess(record)) return humanizeStatus(getDeliveryStatus(record));
  if (hasDeliveryError(record)) return 'Dropped';
  if (record.xcoStatus === 'SELECTED_FOR_DISPATCH') return 'No event';
  return 'Not sent';
};

const renderTemplate = (record: JourneyRecord): React.ReactNode =>
  (record.templateID ? (
    <Tooltip title={record.templateID}>
      <Typography.Link
        href={getContentUrl(record.templateID)}
        target="_blank"
        rel="noreferrer"
      >
        {compactText(record.templateID, 30)}
      </Typography.Link>
    </Tooltip>
  ) : (
    <Typography.Text type="secondary">-</Typography.Text>
  ));

const buildFunnelStages = (record: JourneyRecord, token: any): FunnelStage[] => {
  const rfStatus = relevancyTone(record);
  const xcoStatus = xcoTone(record);
  const deliveryStatus = deliveryTone(record);

  return [
    {
      key: 'rf',
      title: 'Relevance filter',
      statusLabel: getRelevancyStageLabel(record),
      tone: rfStatus,
      content: (
        <DetailCard token={token}>
          <DetailDescriptions
            items={[
              {
                key: 'submission',
                label: 'RF submission',
                children: formatDateOnly(record.rfSubmissionDate),
              },
              {
                key: 'status',
                label: 'RF status',
                children: statusTag(record.rfStatus, ['passed_rf']),
              },
              {
                key: 'campaignType',
                label: 'Campaign Type',
                children: getCampaignType(record.modelSource),
              },
              {
                key: 'score',
                label: 'Score',
                children: scoreValue(record.score),
              },
              {
                key: 'planned',
                label: 'Planned dispatch',
                children: formatDate(record.rfPlannedDispatchedTime),
              },
            ]}
          />
        </DetailCard>
      ),
    },
    {
      key: 'xco',
      title: 'XCO',
      statusLabel: getXcoStageStatusLabel(record),
      tone: xcoStatus,
      content: (
        <DetailCard token={token}>
          <DetailDescriptions
            items={[
              {
                key: 'xcoStatus',
                label: 'XCO status',
                children: record.xcoStatus ? (
                  <StatusTag tone={xcoStatus}>
                    {humanizeStatus(record.xcoStatus)}
                  </StatusTag>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
              },
              {
                key: 'dailyRetries',
                label: 'Daily retries',
                children: getDailyRetries(record),
              },
              {
                key: 'actual',
                label: 'XCO dispatch',
                children: formatDate(record.dispatchedTime),
              },
            ]}
          />
        </DetailCard>
      ),
    },
    {
      key: 'delivery',
      title: 'Delivery',
      statusLabel: getDeliveryStageLabel(record),
      tone: deliveryStatus,
      content: (
        <DetailCard token={token}>
          <DetailDescriptions
            items={[
              {
                key: 'status',
                label: 'Delivery status',
                children: statusTag(getDeliveryStatus(record), DELIVERY_SUCCESS_STATUSES),
              },
              {
                key: 'messageId',
                label: 'Message ID',
                children: <TruncatedId value={record.hedwigMessageID} />,
              },
              {
                key: 'streamTime',
                label: 'Delivery time',
                children: formatDate(record.hedwigStreamtime),
              },
              {
                key: 'eventType',
                label: 'Event type',
                children: record.hedwigEventtype ? (
                  <Typography.Text code>{record.hedwigEventtype}</Typography.Text>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
              },
              {
                key: 'http',
                label: 'HTTP code',
                children: httpCodeTag(record.hedwigHTTPCode),
              },
              {
                key: 'service',
                label: 'Calling service',
                children: textOrMissing(record.hedwigCallingService),
              },
              {
                key: 'message',
                label: 'Message',
                children: record.hedwigMessage ? (
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {record.hedwigMessage}
                      </span>
                    }
                  >
                    <Typography.Paragraph
                      copyable={{ text: record.hedwigMessage }}
                      ellipsis={{ rows: 3 }}
                      style={{
                        marginBottom: 0,
                        textAlign: 'left',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {record.hedwigMessage}
                    </Typography.Paragraph>
                  </Tooltip>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
              },
            ]}
          />
        </DetailCard>
      ),
    },
  ];
};

interface DebuggingToolsDrawerProps {
  record: JourneyRecord | null;
  open: boolean;
  onClose: () => void;
}

const DebuggingToolsDrawer: React.FC<DebuggingToolsDrawerProps> = ({ record, open, onClose }) => {
  const { token } = theme.useToken();
  const outcome = record ? getOutcome(record) : null;
  const titleStatus = record ? getTitleStatus(record) : null;
  const funnelStages = record ? buildFunnelStages(record, token) : [];
  const failedStageIndex = funnelStages.findIndex(stage => stage.tone === 'error');
  const currentStageIndex = failedStageIndex >= 0
    ? failedStageIndex
    : Math.max(funnelStages.length - 1, 0);
  const campaignUrl = record ? getCampaignUrl(record.trackingID, record.ucdCampaignID) : '';

  return (
    <Drawer
      title={
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Title level={4} style={{ margin: 0 }}>
            CommsTrace detail
          </Typography.Title>
          {titleStatus && <StatusTag tone={titleStatus.tone}>{titleStatus.label}</StatusTag>}
        </Flex>
      }
      width="min(1120px, calc(100vw - 32px))"
      open={open}
      onClose={onClose}
      zIndex={1100}
      styles={{
        body: {
          background: token.colorBgLayout || '#f7f8fa',
          padding: '20px 24px',
        },
        content: {
          background: token.colorBgLayout || '#f7f8fa',
        },
        header: {
          background: token.colorBgLayout || '#f7f8fa',
        },
      }}
    >
      {record && (
        <Space direction="vertical" size={20} style={{ display: 'flex' }}>
          <div
            style={{
              display: 'grid',
              gap: 12,
              gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            }}
          >
            <MetadataItem label="User ID" token={token}>
              <Typography.Text strong>{record.userID || '-'}</Typography.Text>
            </MetadataItem>
            <MetadataItem label="Campaign" token={token}>
              {record.ucdCampaignName ? (
                <Tooltip title={record.ucdCampaignName}>
                  <Typography.Link
                    href={campaignUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {compactText(record.ucdCampaignName, 34)}
                  </Typography.Link>
                </Tooltip>
              ) : (
                <Typography.Text type="secondary">-</Typography.Text>
              )}
            </MetadataItem>
            <MetadataItem label="Tracking ID" token={token}>
              <TruncatedId value={record.trackingID} />
            </MetadataItem>
            <MetadataItem label="Template ID" token={token}>
              {renderTemplate(record)}
            </MetadataItem>
          </div>

          <div
            style={{
              alignItems: 'start',
              display: 'grid',
              gap: 20,
              gridTemplateColumns: 'minmax(0, 1fr) 320px',
            }}
          >
            <Steps
              current={currentStageIndex}
              direction="vertical"
              items={funnelStages.map(stage => ({
                key: stage.key,
                title: (
                  <StageTitle
                    title={stage.title}
                    statusLabel={stage.statusLabel}
                    tone={stage.tone}
                  />
                ),
                description: stage.content,
                icon: <StepMarker tone={stage.tone} token={token} />,
                status: getStepStatus(stage.tone),
              }))}
            />

            <Space direction="vertical" size={16} style={{ display: 'flex' }}>
              <Card
                title="Outcome"
                size="small"
                styles={{ body: { padding: 16 } }}
              >
                <Space direction="vertical" size={12} style={{ display: 'flex' }}>
                  <Flex align="center" gap={8} wrap="wrap">
                    <Typography.Title
                      level={3}
                      style={{ margin: 0 }}
                      type={outcome && outcome.tone === 'error' ? 'danger' : undefined}
                    >
                      {outcome ? outcome.result : '-'}
                    </Typography.Title>
                    {outcome && <StatusTag tone={outcome.tone}>{outcome.reason}</StatusTag>}
                  </Flex>
                  <DetailDescriptions
                    items={[
                      {
                        key: 'stage',
                        label: 'Dropped at',
                        children: outcome ? outcome.droppedAt : '-',
                      },
                      {
                        key: 'time',
                        label: 'Timestamp',
                        children: outcome ? outcome.timestamp : '-',
                      },
                    ]}
                  />
                </Space>
              </Card>

              <Card
                title="Trace summary"
                size="small"
                styles={{ body: { padding: 16 } }}
              >
                <DetailDescriptions
                  items={[
                    { key: 'total', label: 'Total stages', children: funnelStages.length },
                    {
                      key: 'passed',
                      label: 'Passed',
                      children: funnelStages.filter(stage => stage.tone === 'success').length,
                    },
                    {
                      key: 'pending',
                      label: 'Pending',
                      children: funnelStages.filter(stage => stage.tone === 'warning').length,
                    },
                    {
                      key: 'dropped',
                      label: 'Dropped',
                      children: funnelStages.filter(stage => stage.tone === 'error').length,
                    },
                  ]}
                />
              </Card>

              <Card
                title="Debug actions"
                size="small"
                styles={{ body: { padding: 16 } }}
              >
                <Space direction="vertical" size={8} style={{ display: 'flex' }}>
                  <Button
                    block
                    href={campaignUrl}
                    icon={<LaunchUniversal />}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open campaign
                  </Button>
                  <Button
                    block
                    disabled={!record.templateID}
                    href={record.templateID ? getContentUrl(record.templateID) : undefined}
                    icon={<DocumentOutlined />}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open template
                  </Button>
                </Space>
              </Card>
            </Space>
          </div>
        </Space>
      )}
    </Drawer>
  );
};

export default DebuggingToolsDrawer;
